import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import {
	IAddConnectorProps,
	IAddConnectorResponse,
	IConnectorFreeswitchAssistant,
	IConnectorFreeswitchRobot,
	IConnectorMax,
	IConnectorNats,
	IConnectorWebBase,
	IConnectorWebChat,
	IDeleteConnectorProps,
	IEditConnectorProps,
	IHandlerResponse,
	ISesConnectorState,
	TChannel,
	TConnectors,
	TSubtypeFreeswitsh,
	TTypeWeb,
} from "@/types/ses/sesConnectorTypes";
import { axiosApi, getResponseError, getPostfixWithRobotId, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "../../constants/api_endpoints";
import { IResponse, RequestStatus, ResponseStatus } from "../../types/statusTypes";

const initialState: ISesConnectorState = {
	connectorList: {
		data: [],
		currentConnector: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	addingConnector: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
	},
	editingConnector: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	deletingConnector: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	handlerList: {
		data: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// список коннекторов
export const getConnectorList = createAsyncThunk("sesConnector/list", async (robotId: string, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<TConnectors[] | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.CONNECTOR_LIST}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// добавление коннектора
export const addConnector = createAsyncThunk(
	"sesConnector/add",
	async ({ robotId, connectorName, channel, params }: IAddConnectorProps, { getState, rejectWithValue }) => {
		try {
			const response: AxiosResponse<IAddConnectorResponse> = await axiosApi.post(
				`${URL_SES}/${ENDPOINTS_SES.CONNECTOR_ADD}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}`,
				{
					name: connectorName,
					type: channel,
					params: JSON.stringify(params),
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// редактирование коннектора
export const editConnector = createAsyncThunk("sesConnector/edit", async ({ robotId, connectorId, name, params }: IEditConnectorProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.CONNECTOR_MODIFY}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${connectorId}`,
			{
				name: name,
				params: JSON.stringify(params),
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// удаление коннектора
export const deleteConnector = createAsyncThunk("sesConnector/delete", async ({ robotId, connectorId }: IDeleteConnectorProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(
			`${URL_SES}/${ENDPOINTS_SES.CONNECTOR_DELETE}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${connectorId}`
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// список обработчиков
export const getHandlerList = createAsyncThunk("sesHandler/list", async (connectorType: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IHandlerResponse[] | IResponse> = await axiosApi.get(`${URL_SES}/${ENDPOINTS_SES.HANDLER_LIST}/${connectorType}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesConnectorSlice = createSlice({
	name: "sesConnector",
	initialState,
	reducers: {
		// добавить текущий коннектор
		addCurrentConnector: (state, action: PayloadAction<null | TConnectors>) => {
			state.connectorList.currentConnector = action.payload;
		},
		// изменение названия коннектора
		changeConnectorName: (state, action: PayloadAction<{ newName: string }>) => {
			const { newName } = action.payload;
			if (state.connectorList.currentConnector) {
				state.connectorList.currentConnector.name = newName;
			}
		},
		// изменение строковых значений коннектора мессенджера
		changeStringValuesMessengerConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk">;
				field: "groupId" | "botName" | "token" | "server" | "reset";
				value: string;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel && field !== "groupId") {
				state.connectorList.currentConnector.params[field] = value;
			} else if (state.connectorList.currentConnector?.type === "vk") {
				state.connectorList.currentConnector.params[field] = value;
			}
		},
		// изменение типа коннектора freeswitch
		changeTypeFreeswitchConnector: (state, action: PayloadAction<{ serviceMode: TSubtypeFreeswitsh }>) => {
			const { serviceMode } = action.payload;
			if (state.connectorList.currentConnector?.type === "freeswitch") {
				const destination = state.connectorList.currentConnector.params.destination;
				const autostart = state.connectorList.currentConnector.params.autostart;
				switch (serviceMode) {
					case "robot":
						state.connectorList.currentConnector.params = { serviceMode, destination, assistant: "", autostart, endpoint: "" };
						break;
					case "assistant":
						state.connectorList.currentConnector.params = { serviceMode, destination, cc_destination: "", autostart };
						break;
					default:
						const _: never = serviceMode;
						console.warn(_);
						break;
				}
			}
		},
		// изменение строковых значений коннектора freeswitch
		changeStringValuesFreeswitchConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "freeswitch">;
				field: "destination" | "assistant" | "cc_destination" | "endpoint";
				value: string;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				if (state.connectorList.currentConnector.params.serviceMode === "robot" && field !== "cc_destination") {
					state.connectorList.currentConnector.params[field] = value;
				} else if (state.connectorList.currentConnector.params.serviceMode === "assistant" && field !== "assistant" && field !== "endpoint") {
					state.connectorList.currentConnector.params[field] = value;
				}
			}
		},
		// изменение булевых значeний коннектора freeswitch
		changeBooleanValuesFreeswitchConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "freeswitch">;
				field: Extract<keyof (IConnectorFreeswitchRobot | IConnectorFreeswitchAssistant), "autostart">;
				value: boolean;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[field] = value;
			}
		},
		// изменение типа коннектора web
		changeTypeWebConnector: (state, action: PayloadAction<{ type: TTypeWeb }>) => {
			const { type } = action.payload;
			if (state.connectorList.currentConnector?.type === "web") {
				switch (type) {
					case "chat":
						state.connectorList.currentConnector.params = {
							...state.connectorList.currentConnector.params,
							type, // смена типа
							audioAnswers: true,
							sendFile: true,
							microphone: true,
							audioCall: true,
							variables: {},
						};
						break;
					case "assistant":
						state.connectorList.currentConnector.params = {
							type, // смена типа
							domain: state.connectorList.currentConnector.params.domain,
							token: state.connectorList.currentConnector.params.token,
							host: state.connectorList.currentConnector.params.host,
							color: state.connectorList.currentConnector.params.color,
							header: state.connectorList.currentConnector.params.header,
							inittext: state.connectorList.currentConnector.params.inittext,
							language: state.connectorList.currentConnector.params.inittext,
						};
						break;
					default:
						const _: never = type;
						console.warn(_);
						break;
				}
			}
		},
		// изменение строковых значений коннектора web
		changeStringValuesWebConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "web">;
				field: Extract<keyof IConnectorWebBase, "domain" | "token" | "color" | "header" | "inittext" | "language">;
				value: string;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[field] = value;
			}
		},
		// изменение числовых значений коннектора мессенджера
		changeNumberValuesMessengerConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk">;
				field: Extract<keyof IConnectorMax, "pollingInterval">;
				value: number;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[field] = value;
			}
		},
		// изменение булевых значeний коннектора web
		changeBooleanValuesWebConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "web">;
				field: Extract<keyof IConnectorWebChat, "audioAnswers" | "sendFile" | "microphone" | "audioCall">;
				value: boolean;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel && state.connectorList.currentConnector.params.type === "chat") {
				state.connectorList.currentConnector.params[field] = value;
			}
		},
		// изменение фразы активатора коннектора мессенджера
		changePhraseActivatorMessengerConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk">;
				phraseIdx: number;
				phrase: string;
			}>
		) => {
			const { channel, phraseIdx, phrase } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params.activators.splice(phraseIdx, 1, phrase);
			}
		},
		// удаление фразы активатора коннектора мессенджера
		deletePhraseActivatorMessengerConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk">;
				phraseIdx: number;
			}>
		) => {
			const { channel, phraseIdx } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params.activators.splice(phraseIdx, 1);
			}
		},
		// изменение сервера коннектора web
		changeServerWebConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "web">;
				serverIdx: number;
				server: string;
			}>
		) => {
			const { channel, serverIdx, server } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params.host.splice(serverIdx, 1, server);
			}
		},
		// удаление сервера коннектора web
		deleteServerWebConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "web">;
				serverIdx: number;
			}>
		) => {
			const { channel, serverIdx } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params.host.splice(serverIdx, 1);
			}
		},
		// изменение строковых значений коннектора nats
		changeStringValuesNatsConnector: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "nats">;
				field: keyof IConnectorNats;
				value: string;
			}>
		) => {
			const { channel, field, value } = action.payload;
			if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[field] = value;
			}
		},
		// добавление поля в настройку коннектора
		addFieldToConnectorSetting: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk" | "web">;
				setting: "variables" | "whitelist";
				field: string;
			}>
		) => {
			const { channel, setting, field } = action.payload;
			if (channel === "web") {
				if (state.connectorList.currentConnector?.type === channel && setting === "variables" && state.connectorList.currentConnector.params.type === "chat") {
					state.connectorList.currentConnector.params[setting][field] = "";
				}
			} else if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[setting][field] = "";
			}
		},
		// изменение названия поля настройки коннектора
		changeFieldNameInConnectorSettings: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk" | "web">;
				setting: "variables" | "whitelist";
				oldName: string;
				newName: string;
			}>
		) => {
			const { channel, setting, oldName, newName } = action.payload;
			if (channel === "web") {
				if (state.connectorList.currentConnector?.type === channel && setting === "variables" && state.connectorList.currentConnector.params.type === "chat") {
					state.connectorList.currentConnector.params[setting][newName] = state.connectorList.currentConnector.params[setting][oldName];
					delete state.connectorList.currentConnector.params[setting][oldName];
				}
			} else if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[setting][newName] = state.connectorList.currentConnector.params[setting][oldName];
				delete state.connectorList.currentConnector.params[setting][oldName];
			}
		},
		// изменение значения настройки коннектора
		changeConnectorSettingValue: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk" | "web">;
				setting: "variables" | "whitelist";
				field: string;
				value: string;
			}>
		) => {
			const { channel, setting, field, value } = action.payload;
			if (channel === "web") {
				if (state.connectorList.currentConnector?.type === channel && setting === "variables" && state.connectorList.currentConnector.params.type === "chat") {
					state.connectorList.currentConnector.params[setting][field] = value;
				}
			} else if (state.connectorList.currentConnector?.type === channel) {
				state.connectorList.currentConnector.params[setting][field] = value;
			}
		},
		// удаление поля из настройки коннектора
		deleteFieldToConnectorSetting: (
			state,
			action: PayloadAction<{
				channel: Extract<TChannel, "max" | "telegram" | "vk" | "web">;
				setting: "variables" | "whitelist";
				field: string;
			}>
		) => {
			const { channel, setting, field } = action.payload;
			if (channel === "web") {
				if (state.connectorList.currentConnector?.type === channel && setting === "variables" && state.connectorList.currentConnector.params.type === "chat") {
					delete state.connectorList.currentConnector.params[setting][field];
				}
			} else if (state.connectorList.currentConnector?.type === channel) {
				delete state.connectorList.currentConnector.params[setting][field];
			}
		},
		// очистка state
		clearState: (state) => {
			state.connectorList = initialState.connectorList;
			state.addingConnector = initialState.addingConnector;
			state.editingConnector = initialState.editingConnector;
			state.deletingConnector = initialState.deletingConnector;
			state.handlerList = initialState.handlerList;
		},
		// очистка списка коннекторов
		clearConnectorList: (state) => {
			state.connectorList = initialState.connectorList;
		},
		// очистка статуса добавления коннектора
		clearAddingConnector: (state) => {
			state.addingConnector = initialState.addingConnector;
		},
		// очистка статуса изменения коннектора
		clearEditingConnector: (state) => {
			state.editingConnector = initialState.editingConnector;
		},
		// очистка статуса удаления коннектора
		clearDeletingConnector: (state) => {
			state.deletingConnector = initialState.deletingConnector;
		},
		// очистка списка обработчиков
		clearHandlerList: (state) => {
			state.handlerList = initialState.handlerList;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getConnectorList.pending, (state) => {
				state.connectorList.status = RequestStatus.LOADING;
			})
			.addCase(getConnectorList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.connectorList.status = RequestStatus.IDLE;
					state.connectorList.data = action.payload;
				} else state.connectorList.status = RequestStatus.FAILED;
			})
			.addCase(getConnectorList.rejected, (state, action: PayloadAction<unknown>) => {
				state.connectorList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.connectorList.error = error;
				state.connectorList.message = message;
			})
			.addCase(addConnector.pending, (state) => {
				state.addingConnector.status = RequestStatus.LOADING;
			})
			.addCase(addConnector.fulfilled, (state, action) => {
				if (action.payload) {
					state.addingConnector.status = RequestStatus.IDLE;
					state.addingConnector.error = action.payload.error;
					state.addingConnector.message = action.payload.message;
					if (action.payload.id) {
						state.addingConnector.id = action.payload.id;
						// const { connectorParams } = action.meta.arg;
						// const connectors = state.robot.data?.connectors;
						// if (connectors) {
						// 	const connector = connectors.find((conn) => conn.name === connectorParams.name && conn.type === connectorParams.type && !conn.id);
						// 	if (connector) {
						// 		connector.id = action.payload.id;
						// 	}
						// }
					}
				} else state.addingConnector.status = RequestStatus.FAILED;
			})
			.addCase(addConnector.rejected, (state, action: PayloadAction<unknown>) => {
				state.addingConnector.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.addingConnector.error = error;
				state.addingConnector.message = message;
			})
			.addCase(editConnector.pending, (state) => {
				state.editingConnector.status = RequestStatus.LOADING;
			})
			.addCase(editConnector.fulfilled, (state, action) => {
				if (action.payload) {
					state.editingConnector.status = RequestStatus.IDLE;
					state.editingConnector.error = action.payload.error;
					state.editingConnector.message = action.payload.message;
				} else state.editingConnector.status = RequestStatus.FAILED;
			})
			.addCase(editConnector.rejected, (state, action: PayloadAction<unknown>) => {
				state.editingConnector.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.editingConnector.error = error;
				state.editingConnector.message = message;
			})
			.addCase(deleteConnector.pending, (state) => {
				state.deletingConnector.status = RequestStatus.LOADING;
			})
			.addCase(deleteConnector.fulfilled, (state, action) => {
				if (action.payload) {
					state.deletingConnector.status = RequestStatus.IDLE;
					state.deletingConnector.error = action.payload.error;
					state.deletingConnector.message = action.payload.message;
				} else state.deletingConnector.status = RequestStatus.FAILED;
			})
			.addCase(deleteConnector.rejected, (state, action: PayloadAction<unknown>) => {
				state.deletingConnector.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.deletingConnector.error = error;
				state.deletingConnector.message = message;
			})
			.addCase(getHandlerList.pending, (state) => {
				state.handlerList.status = RequestStatus.LOADING;
			})
			.addCase(getHandlerList.fulfilled, (state, action) => {
				if (Array.isArray(action.payload)) {
					state.handlerList.status = RequestStatus.IDLE;
					state.handlerList.data = action.payload;
				} else state.handlerList.status = RequestStatus.FAILED;
			})
			.addCase(getHandlerList.rejected, (state, action: PayloadAction<unknown>) => {
				state.handlerList.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.handlerList.error = error;
				state.handlerList.message = message;
			});
	},
});

export const {
	addCurrentConnector,
	changeConnectorName,
	changeStringValuesMessengerConnector,
	changeTypeFreeswitchConnector,
	changeStringValuesFreeswitchConnector,
	changeBooleanValuesFreeswitchConnector,
	changeTypeWebConnector,
	changeStringValuesWebConnector,
	changeNumberValuesMessengerConnector,
	changeBooleanValuesWebConnector,
	changePhraseActivatorMessengerConnector,
	deletePhraseActivatorMessengerConnector,
	changeServerWebConnector,
	deleteServerWebConnector,
	changeStringValuesNatsConnector,
	addFieldToConnectorSetting,
	changeFieldNameInConnectorSettings,
	changeConnectorSettingValue,
	deleteFieldToConnectorSetting,
	clearState,
	clearConnectorList,
	clearAddingConnector,
	clearEditingConnector,
	clearDeletingConnector,
	clearHandlerList,
} = sesConnectorSlice.actions;

export const selectConnectorList = (state: RootState) => state.sesConnector.connectorList;
export const selectAddingConnector = (state: RootState) => state.sesConnector.addingConnector;
export const selectEditingConnector = (state: RootState) => state.sesConnector.editingConnector;
export const selectDeletingConnector = (state: RootState) => state.sesConnector.deletingConnector;
export const selectHandlerList = (state: RootState) => state.sesConnector.handlerList;

export default sesConnectorSlice.reducer;
